import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { useAuth } from '../contexts/AuthContext.jsx'
import { DayGrid, WeekGrid } from '../components/BoardGrid.jsx'
import { FleetDayTable, FleetWeekRows, UNGROUPED, useFleet } from '../components/FleetBand.jsx'
import TimeOffModal from '../components/TimeOffModal.jsx'
import Modal from '../components/Modal.jsx'
import PrintDialog from '../components/PrintDialog.jsx'
import BoardOrderDialog from '../components/BoardOrderDialog.jsx'
import InternalNotesDrawer from '../components/InternalNotesDrawer.jsx'
import ConflictsDrawer from '../components/ConflictsDrawer.jsx'
import MonthGrid from '../components/MonthGrid.jsx'
import DayTimeOffModal from '../components/DayTimeOffModal.jsx'
import CopyDayModal from '../components/CopyDayModal.jsx'
import HolidayNote from '../components/HolidayNote.jsx'
import SplitButton from '../components/SplitButton.jsx'
import RichText, { AddNoteButton } from '../components/RichText.jsx'
import { MultiSelectDropdown } from '../components/Dropdown.jsx'
import { SettingsRow } from '../components/SettingsLayout.jsx'
import Icon from '../components/Icon.jsx'
import GlyphTile from '../components/GlyphTile.jsx'
import { TILE } from '../lib/tileLook.js'
import { DateField, DayNav } from '../components/DatePicker.jsx'
import { useToast, useUndo } from '../components/Toast.jsx'
import { revealCell } from '../lib/reveal.js'
import { readJSON, writeJSON, readSessionJSON, writeSessionJSON } from '../lib/localStore.js'
import { applyBoardOrder, orderFor, readOrder } from '../lib/boardOrder.js'
import { PEOPLE_BAND_ID, toPeopleBoard } from '../lib/peopleBoard.js'
import { conflictsOn, openConflicts } from '../lib/conflicts.js'
import { toggleInSet, toCsv, fromCsv } from '../lib/sets.js'
import outlookIcon from '../assets/outlook.webp'
import {
  addMonths,
  formatDate,
  formatMonth,
  formatShort,
  lastOfMonth,
  monthGrid,
  monthOf,
  workingWeek,
  workingFortnight,
} from '../lib/dates.js'
// The same module the server gates on — not a copy of it. See shared/scope.js.
import {
  canCreate,
  canMutate,
  isOverride,
  canWriteBoard,
  teamStaffIdsFromBoard,
} from '../../../shared/scope.js'

// Board display settings — the date, the view, which teams are collapsed, which are pinned
// aside and how wide — live in the URL while you're on the page, the same way a search or a
// report page would carry its own state: it makes the current board a link you can bookmark
// or hand to someone else, and it means Back/Forward move through what you were looking at
// rather than just which route you were on. localStorage is the fallback underneath that,
// used ONLY when the URL arrives with none of this on it — landing on a bare /board from the
// nav bar, say — so the last thing you had set up is what a plain visit restores, and the URL
// then picks it right back up (see the sync effect below). Collapse and pins are keyed by
// MODE in that fallback — a CAT team key and a PM team key are different keyspaces, and a
// Normal-mode pin has nothing to restore in CAT mode or the reverse. The date is remembered
// too, deliberately: a scheduler working three weeks out expects to come back to three weeks
// out, not to today, every time they check something on another page and click back.
const VIEW_KEY = 'fgs-board-view'
const DATE_KEY = 'fgs-board-date'
const SIDE_WIDTHS_KEY = 'fgs-board-side-widths'
const GROUP_BY_KEY = 'fgs-board-group-by'
const FLEET_KEY = 'fgs-board-fleet-collapsed'
const collapsedKey = (mode) => `fgs-board-collapsed-${mode}`
const pinsKey = (mode) => `fgs-board-pins-${mode}`
// The row order from Board view settings. Session storage, not local, and never the URL:
// it is asked to last the session, and a link is for handing to somebody else, who should
// see the board in their own order rather than in this one. Keyed by company and person
// because signing out reloads the same tab, and session storage survives that.
const orderKey = (tenantId, userId) => `fgs-board-order:${tenantId}:${userId}`

// Graph carries at most a 4MB message in one sendMail, base64 included — the server refuses
// anything past this, so the dialog says so before a person waits for the send to fail.
// Keep in step with MAX_ATTACH_BYTES in server/src/repos/notify.js.
const MAX_ATTACH_BYTES = 3 * 1024 * 1024
const fileSize = (n) =>
  n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`

// Frozen empties for the day dialog's inert props. Module-level so their identity is
// stable across renders — a fresh {} each time would re-render the grid for nothing.
// The composed email is a fragment; a document around it stops the iframe applying quirks
// mode and gives the preview the same white ground a mail client would.
const previewDoc = (html) =>
  `<!doctype html><html><head><meta charset="utf-8">` +
  `<style>body{margin:0;padding:18px;background:#fff;` +
  `font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif}</style>` +
  `</head><body>${html}</body></html>`

// How long the revealed cell stays marked: two pulses of the reveal-pulse keyframe, plus a
// beat to read it. Kept in step with the animation in styles.css.
const REVEAL_MS = 1900

const EMPTY_SET = new Set()
const EMPTY_PINS = { left: [], right: [] }
const NOOP = () => {}

export default function Board() {
  const { user, tenant, mode: currentMode, settings, can, modeName, roleLabel, assetsName, assetUnit } = useAuth()
  const qc = useQueryClient()
  const toast = useToast()
  const [searchParams, setSearchParams] = useSearchParams()
  const [date, setDate] = useState(() => searchParams.get('d') || readJSON(DATE_KEY, null) || currentMode?.today)
  const [view, setView] = useState(() => {
    // Comes from a URL somebody may have typed and from localStorage that may predate a
    // view being renamed, so it is validated rather than trusted — an unknown value used to
    // fall through to the week grid rendering a single day.
    const wanted = searchParams.get('v') || readJSON(VIEW_KEY, 'day')
    return ['day', 'week', '2week', 'month'].includes(wanted) ? wanted : 'day'
  })
  const [editing, setEditing] = useState(null)
  // CAT team reorganisation: a pending "cut" staff member, and which teams are collapsed.
  // Both are local UI state — cat_team_members is the source of truth, this is just what
  // the person looking at the board is in the middle of doing.
  const [printing, setPrinting] = useState(false) // the print/copy/save dialog
  const [orderOpen, setOrderOpen] = useState(false) // Board view settings
  const [notesOpen, setNotesOpen] = useState(false) // the internal-notes drawer
  const [conflictsOpen, setConflictsOpen] = useState(false) // the conflicts drawer
  /**
   * The cell the notes drawer last pointed at: { entryId, cellKey, token }.
   *
   * Its own state, deliberately not `editing`. `editing` means "an editor is open over this
   * cell" — writing it here would pop the editor open on a cell the person only asked to be
   * shown, and on the day grid a cell whose key equals `editing` refuses its own click, so
   * the highlight would have left it dead to the touch.
   *
   * `token` counts up on every reveal so clicking the same note twice re-runs the scroll
   * rather than being swallowed as "no change".
   */
  const [reveal, setReveal] = useState(null)
  const revealDone = useRef(null)
  /**
   * A counter that only ever goes up, kept in a ref rather than derived from the previous
   * reveal. Deriving it meant the blink's own auto-clear reset it: with `reveal` back to
   * null, the next click computed token 1 again — a number revealDone had already recorded —
   * so the effect took its already-done branch and skipped the scroll. The cell still got
   * marked, because the class comes straight off the state, which is exactly what the second
   * click looked like: highlighted, but never brought into view.
   */
  const revealSeq = useRef(0)
  const [notify, setNotify] = useState(null) // the preview being confirmed, or null
  const [notifyBusy, setNotifyBusy] = useState(false)
  const [previewBusy, setPreviewBusy] = useState(false)
  const [cut, setCut] = useState(null) // { staffId, fromGroupId } | null
  const [moveHistory, setMoveHistory] = useState([]) // [{ staffId, fromGroupId, toGroupId }]
  const [clip, setClip] = useState(null) // { label, jobTypeId } — a copied cell
  // "Copy this day to…": whose day, which day, and the board it was opened from (the day
  // dialog over the month view reads its own).
  const [copyDayFor, setCopyDayFor] = useState(null) // { staffId, date, source }
  // { staffId, entry } — entry is the absence being changed, or null when adding a new one.
  const [timeOffFor, setTimeOffFor] = useState(null)
  // A date, when the month view's "book people off on this day" dialog is open.
  const [timeOffDay, setTimeOffDay] = useState(null)
  // A date, when the month view has opened that day's board in a dialog.
  const [dayModal, setDayModal] = useState(null)
  // Which people the month view is narrowed to. Empty means everyone, so the common case
  // costs nothing to represent and "cleared" and "nobody chosen" are the same state.
  const [people, setPeople] = useState(() => new Set())
  // Whether the fleet band is folded away. One flag, not one per mode: the fleet is the same
  // rows whichever board is running.
  // Which fleet bands are shut, by asset-group id. A Set rather than the single boolean this
  // was, because the fleet is now one band per group and they collapse independently — the
  // same shape and the same storage habit as the crew bands above it. A stored `true` from
  // the old boolean is not an array and simply reads as "nothing collapsed".
  const [fleetCollapsed, setFleetCollapsed] = useState(() => {
    const saved = readJSON(FLEET_KEY, [])
    return new Set(Array.isArray(saved) ? saved : [])
  })
  const toggleFleetBand = (key) => setFleetCollapsed((prev) => toggleInSet(prev, key))
  useEffect(() => {
    writeJSON(FLEET_KEY, [...fleetCollapsed])
  }, [fleetCollapsed])
  /**
   * How the board's ROWS are organised: under their crew, or one row per person with the
   * crew shown as colour on each job instead.
   *
   * Group mode only — a Team-mode board is already one row per person, grouped by the PM
   * who owns them, and there is no second crew colour to carry. Validated on the way in the
   * same way `view` is, since it arrives from a URL somebody may have typed.
   */
  const [groupBy, setGroupBy] = useState(() => {
    const wanted = searchParams.get('gb') || readJSON(GROUP_BY_KEY, 'group')
    return wanted === 'people' ? 'people' : 'group'
  })
  /**
   * This person's own order for the rows, from Board view settings — see lib/boardOrder.js.
   * Re-read when the key changes, which is only ever the person or the company, and written
   * only when saved: an effect writing it back would copy one person's order under the next
   * one's key on the render the key changed.
   */
  const sessionOrderKey = tenant?.id && user?.id ? orderKey(tenant.id, user.id) : null
  const [order, setOrder] = useState(() => readOrder(sessionOrderKey && readSessionJSON(sessionOrderKey, null)))
  useEffect(() => {
    setOrder(readOrder(sessionOrderKey && readSessionJSON(sessionOrderKey, null)))
  }, [sessionOrderKey])
  const saveOrder = (next) => {
    const clean = readOrder(next)
    setOrder(clean)
    if (sessionOrderKey) writeSessionJSON(sessionOrderKey, clean)
  }
  // Which side each pinned team sits on can have more than one entry per side now — they
  // stack, sharing that side's one resizer and width, rather than each getting its own panel.
  // Read here with a mode-agnostic placeholder; the mode-scoped effect just below replaces
  // pins itself the moment `mode` is known, which on first paint is one render later at most.
  const [sideWidths, setSideWidths] = useState(() => {
    const clamp = (n) => (Number.isFinite(n) && n >= 260 && n <= 720 ? n : 380)
    const lw = Number(searchParams.get('lw'))
    const rw = Number(searchParams.get('rw'))
    if (lw || rw) return { left: clamp(lw), right: clamp(rw) }
    const saved = readJSON(SIDE_WIDTHS_KEY, { left: 380, right: 380 })
    return { left: clamp(saved.left), right: clamp(saved.right) }
  })

  // The control header is sticky (see .board-page .page-head). Publish its measured height so
  // the pinned-team panel can sit below it instead of behind it. Measured rather than assumed
  // because the header wraps: narrow the window and it becomes two rows, and on a phone the
  // segmented controls stack again — every one of those is a different offset.
  const headRef = useRef(null)
  const pageRef = useRef(null)
  useEffect(() => {
    const head = headRef.current
    const page = pageRef.current
    if (!head || !page) return undefined
    const publish = () =>
      page.style.setProperty('--board-head-h', `${Math.round(head.getBoundingClientRect().height)}px`)
    publish()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null
    ro?.observe(head)
    window.addEventListener('resize', publish)
    return () => {
      ro?.disconnect()
      window.removeEventListener('resize', publish)
    }
    // Only needs re-running when the header element itself comes or goes — the board renders
    // a bare "Loading…" page before there is a date. Every later height change (the header
    // wrapping, Notify staff appearing) arrives through the observer.
  }, [date])

  // The board follows the mode in effect for the date being looked at — the same rule the
  // whiteboard uses, so the two never disagree about what Thursday is.
  const { data: dayMode } = useQuery({
    queryKey: ['mode', date],
    queryFn: () => api.get(`/api/mode?on=${date}`),
    enabled: Boolean(date),
  })
  const mode = dayMode?.mode || currentMode?.mode || 'normal'

  // Collapsed teams and pinned-aside teams are keyed by which set of group keys is even
  // valid — a CAT team id means nothing in Normal mode's PM groups, and the reverse — so both
  // are re-read the moment `mode` changes rather than carried over from whichever mode's
  // state a previous render happened to hold. `mode` itself only changes when the date moves
  // across a mode-period boundary or the query resolves, both rare, so this is not the
  // frequent re-read it might look like.
  // The URL is only consulted for the FIRST render's value — a shared or bookmarked link
  // should show what it says. Once `mode` changes after that (the date moved across a mode
  // boundary), whatever the URL still says about a DIFFERENT mode's teams is exactly the
  // stale thing to ignore, so that reset goes to localStorage's per-mode memory instead, same
  // as it always has.
  const [collapsedGroups, setCollapsedGroups] = useState(
    () => new Set(searchParams.has('collapsed') ? fromCsv(searchParams.get('collapsed')) : readJSON(collapsedKey(mode), [])),
  )
  useEffect(() => {
    setCollapsedGroups(new Set(readJSON(collapsedKey(mode), [])))
  }, [mode])
  useEffect(() => {
    writeJSON(collapsedKey(mode), [...collapsedGroups])
  }, [mode, collapsedGroups])

  // { left: string[], right: string[] } — group keys, in stack order. A team can sit in at
  // most one of the two at a time; moving it to the other side is one edit removing it from
  // its old one, not a separate unpin-then-pin (see the team menu in BoardGrid.jsx).
  const [pins, setPins] = useState(() =>
    searchParams.has('pl') || searchParams.has('pr')
      ? { left: fromCsv(searchParams.get('pl')), right: fromCsv(searchParams.get('pr')) }
      : readJSON(pinsKey(mode), { left: [], right: [] }),
  )
  useEffect(() => {
    setPins(readJSON(pinsKey(mode), { left: [], right: [] }))
  }, [mode])
  useEffect(() => {
    writeJSON(pinsKey(mode), pins)
  }, [mode, pins])

  // The URL is the moment-to-moment mirror of all of it — this is the one effect that writes
  // to it, built fresh each time rather than patched, so a stale param from three renders ago
  // (a pin that got removed, a width that got reset) can never linger. `replace` rather than
  // `push`: dragging the resizer alone fires this dozens of times a second, and a Back button
  // that had to click through every pixel of that drag would be useless. localStorage gets
  // the same values, for the bare-/board fallback case the URL itself can't cover.
  useEffect(() => {
    writeJSON(DATE_KEY, date)
    writeJSON(VIEW_KEY, view)
    writeJSON(GROUP_BY_KEY, groupBy)
    writeJSON(SIDE_WIDTHS_KEY, sideWidths)

    const next = new URLSearchParams(searchParams)
    next.set('d', date)
    next.set('v', view)
    const collapsedCsv = toCsv(collapsedGroups)
    collapsedCsv ? next.set('collapsed', collapsedCsv) : next.delete('collapsed')
    const plCsv = toCsv(pins.left)
    plCsv ? next.set('pl', plCsv) : next.delete('pl')
    const prCsv = toCsv(pins.right)
    prCsv ? next.set('pr', prCsv) : next.delete('pr')
    next.set('lw', String(sideWidths.left))
    next.set('rw', String(sideWidths.right))
    // Only when it is not the default, so an ordinary board link stays short and a link
    // without it means what it has always meant.
    groupBy === 'people' ? next.set('gb', 'people') : next.delete('gb')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, view, groupBy, collapsedGroups, pins, sideWidths])

  // Everything above this line is a standing preference; Clear filters puts just the CURRENT
  // mode's collapse and pin state back to nothing, in one click, without touching the date,
  // the view, or the other mode's own saved state.
  const clearFilters = () => {
    setCollapsedGroups(new Set())
    setPins({ left: [], right: [] })
    setPeople(new Set())
  }
  const hasFilters =
    collapsedGroups.size > 0 || pins.left.length > 0 || pins.right.length > 0 || people.size > 0

  const togglePerson = (id) => setPeople((prev) => toggleInSet(prev, id))

  const workDays = mode === 'cat' ? settings?.catWorkDays : settings?.normalWorkDays
  // 'week' and '2week' are the same shape — a run of working days across columns — so they
  // share one span and one grid, and differ only in how far the run reaches.
  const isMulti = view === 'week' || view === '2week'
  const isMonth = view === 'month'
  const month = date ? monthOf(date) : null
  const span = useMemo(() => {
    if (!date) return []
    const weekStart = tenant?.weekStart || 1
    // The month grid runs whole weeks, so it reaches into the neighbouring months at both
    // ends — and those days are drawn, so they have to be fetched too.
    if (view === 'month') return monthGrid(monthOf(date), weekStart)
    const args = [date, weekStart, workDays || [1, 2, 3, 4, 5]]
    return view === '2week' ? workingFortnight(...args) : workingWeek(...args)
  }, [date, view, tenant?.weekStart, workDays])
  // A day at a time on the day view, and a whole span at a time on the others, so the
  // arrows always move by exactly what is on screen. A month is not a number of days —
  // stepping one by adding 30 lands in the wrong month twice a year — so it steps itself.
  const pageStep = view === '2week' ? 14 : view === 'week' ? 7 : 1
  // Clamped to the target month's length: stepping forward from the 31st of January has to
  // land on the 28th of February, not on a '2026-02-31' that nothing downstream can parse.
  const stepMonth = (d, dir) => {
    const ym = addMonths(monthOf(d), dir)
    const last = lastOfMonth(ym)
    const wanted = `${ym}-${d.slice(8)}`
    return wanted > last ? last : wanted
  }

  const from = isMulti || isMonth ? span[0] : date
  const to = isMulti || isMonth ? span[span.length - 1] : date

  const { data: board, isLoading } = useQuery({
    queryKey: ['schedule', mode, from, to],
    queryFn: () => api.get(`/api/schedule?mode=${mode}&from=${from}&to=${to}`),
    enabled: Boolean(from && to),
    // Edits by other people arrive over the live channel (lib/useRealtime.js) within a
    // moment, so this is no longer how the board keeps up — it is the backstop for the
    // case that channel is down and EventSource cannot re-establish it.
    refetchInterval: 90_000,
  })

  // The month's payload spans six weeks and carries no per-day slot counts, so the dialog
  // asks for the one day it is showing rather than trying to slice a month down to it. Same
  // query key shape as the page's own, so an edit made in the dialog invalidates both.
  const { data: dayBoard } = useQuery({
    queryKey: ['schedule', mode, dayModal, dayModal],
    queryFn: () => api.get(`/api/schedule?mode=${mode}&from=${dayModal}&to=${dayModal}`),
    enabled: Boolean(dayModal),
  })

  /**
   * The board as the grids should draw it.
   *
   * Grouped by crew, this is the board exactly as the server sent it. Grouped by PERSON, it
   * is the same data reshaped on the client: every schedulable person becomes a row in one
   * synthetic band, and each job's crew — which the assignment already carries as catTeamId
   * — moves from being the row it sits under to being the colour it is drawn in.
   *
   * A reshape rather than a second grid, and rather than a second query. The grids index
   * rows and cells by renderGroupId, so pointing every assignment at the one band is the
   * whole change; drag-copy, paste, the editor, time off and the fortnight's sticky columns
   * all keep working because none of them knows what a band means. It also cannot drift
   * from the crew-grouped board, because there is only ever one payload.
   *
   * The duplicate-row problem goes with it: somebody borrowed onto another crew has a row
   * under both crews on the grouped board, and exactly one row here.
   */
  const byPeople = groupBy === 'people' && mode === 'cat'

  // The reshape itself is in lib/peopleBoard.js, where it can be tested.
  const asPeopleBoard = useCallback((b) => (byPeople ? toPeopleBoard(b) : b), [byPeople])

  // The rows in this person's order, before anything else is done to the board. The fleet is
  // drawn from this rather than from shownBoard — grouping by person changes nothing about the
  // fleet — and the month's day dialog puts its own day through the same two steps.
  const orderNow = useMemo(() => orderFor(order, mode, byPeople), [order, mode, byPeople])
  const orderedBoard = useMemo(() => applyBoardOrder(board, orderNow), [board, orderNow])
  const shownBoard = useMemo(() => asPeopleBoard(orderedBoard), [orderedBoard, asPeopleBoard])

  /** The crew a band stands for, or null for the synthetic "everyone" band. */
  const crewOfBand = (group) => (group?.id === PEOPLE_BAND_ID ? null : group?.id ?? null)

  /** The real crews, for colouring and for the editor's crew picker. */
  const crews = useMemo(
    () => (board?.groups || []).filter((g) => g.kind === 'cat'),
    [board],
  )

  const teamStaffIds = useMemo(() => teamStaffIdsFromBoard(user, board), [user, board])

  // Only the month view needs this — it draws names against dates rather than rows against
  // people, so it has no staff record in hand at the point it renders one.
  const staffById = useMemo(
    () => new Map((board?.staff || []).map((p) => [p.id, p])),
    [board],
  )

  const peopleOptions = useMemo(
    () => (board?.staff || []).map((p) => ({ value: p.id, label: p.name })),
    [board],
  )

  // What the badge in the header counts. Only the real ones and only the unsettled — see
  // lib/conflicts.js for why a part-day absence is not a fault, and why a count nobody can
  // get to zero is worse than no count at all.
  const conflictCount = useMemo(() => openConflicts(conflictsOn(board)).length, [board])
  // The context shape shared/scope.js expects. The server builds the same thing from the
  // database in boardContext(); here it comes off the payload already on screen.
  const scopeCtx = useMemo(() => ({ mode: board?.mode ?? mode, teamStaffIds }), [board?.mode, mode, teamStaffIds])
  const invalidate = () => qc.invalidateQueries({ queryKey: ['schedule'] })
  // "Removed. Undo" — for the three clicks on this page that take something away. Each hands
  // over the row it just deleted and how to re-create it; see useUndo for why re-creating
  // through the API is the honest way back.
  const undo = useUndo(invalidate)

  // The fleet band's editing state. A hook, so it sits here with the others rather than after
  // the early return below. The handlers it is given are declared further down and hoisted.
  // Who may change who has a vehicle on a day is exactly who may write that day's board; who
  // may make that the standing arrangement is whoever keeps the register.
  // Declared here, above everything that reads it, because a `const` is not hoisted the way a
  // function is: openTimeOff further down used to read it before a later declaration ran and
  // the whole Board threw before drawing a cell.
  const writable = board ? canWriteBoard(user, board) : false

  const fleet = useFleet({
    board,
    writable,
    canManage: Boolean(can?.manageAssets),
    onSetDay: setAssetDay,
    onClearDay: clearAssetDay,
    onSaveDowntime: saveAssetDowntime,
    onDeleteDowntime: deleteAssetDowntime,
    onSetCustody: setAssetCustody,
  })

  /**
   * Returns whether the write actually happened — the week grid's editor is closed on the
   * strength of it. It cannot use `editing` the way the day grid does (it keeps its own
   * editor state), and closing on the click instead would shut the popover on a save the
   * person cancelled at the override prompt, or on one the server refused.
   */
  async function handleSave({ entry, patch, staffId, slot, date: cellDate, group }) {
    try {
      if (entry) {
        if (isOverride(user, scopeCtx, entry)) {
          const who = entry.ownerPmName || 'another PM'
          if (!window.confirm(`${who} booked this. Change it anyway?`)) return false
        }
        await api.patch(`/api/assignments/${entry.id}`, { ...patch, version: entry.version })
      } else {
        const res = await api.post('/api/assignments', {
          mode,
          workDate: cellDate,
          staffUserId: staffId,
          slotIndex: slot,
          // Grouped by crew, the band a cell sits in IS the crew. Grouped by person there is
          // one band holding everybody, so the crew is whatever the editor was told —
          // sending the synthetic band's id would name a crew that does not exist.
          catTeamId: mode === 'cat' ? patch.catTeamId ?? crewOfBand(group) : undefined,
          ...patch,
        })
        if (res?.warning === 'NOT_QUALIFIED') {
          toast.warn('That person is not marked as qualified for that job type.', { title: 'Saved' })
        }
      }
      setEditing(null)
      invalidate()
      return true
    } catch (err) {
      toast.error(err)
      return false
    }
  }

  /** Same contract as handleSave — true only when the entry is really gone. */
  async function handleDelete(entry) {
    if (!entry) {
      setEditing(null)
      return true
    }
    try {
      if (isOverride(user, scopeCtx, entry)) {
        const who = entry.ownerPmName || 'another PM'
        if (!window.confirm(`${who} booked this. Delete it anyway?`)) return false
      }
      await api.del(`/api/assignments/${entry.id}?version=${entry.version}`)
      setEditing(null)
      invalidate()
      undo(`Removed "${entry.label}".`, () =>
        api.post('/api/assignments', {
          mode: entry.mode,
          workDate: entry.date,
          staffUserId: entry.staffId,
          slotIndex: entry.slot,
          label: entry.label,
          jobTypeId: entry.jobTypeId,
          // The crew the job belonged to. On a people board the payload still carries it
          // as catTeamId; the synthetic band is never a crew and never reaches here.
          catTeamId: entry.mode === 'cat' ? entry.catTeamId : undefined,
          internalNote: entry.internalNote,
        }),
      )
      return true
    } catch (err) {
      toast.error(err)
      return false
    }
  }

  async function addColumn() {
    const day = board.days.find((d) => d.date === date)
    try {
      await api.put('/api/slots', { mode, dates: [date], slotCount: (day?.slotCount || 3) + 1 })
      invalidate()
    } catch (err) {
      toast.error(err)
    }
  }

  // Moving a person between CAT teams is one atomic server call, scoped to ONE DATE — it
  // records an override for that day and carries that day's bookings across with it. Other
  // days keep starting from the standing roster, so reorganising Tuesday never quietly
  // reorganises the rest of the month.
  async function moveStaffToTeam(staffId, fromGroupId, toGroupId, { record = true, on = date } = {}) {
    if (fromGroupId === toGroupId) return
    try {
      await api.post('/api/owner/cat-teams/move-member', {
        staffId,
        fromTeamId: fromGroupId,
        toTeamId: toGroupId,
        date: on,
      })
      if (record) setMoveHistory((h) => [...h, { staffId, fromGroupId, toGroupId, on }])
      setCut(null)
      invalidate()

      // Worth confirming out loud: a drag that lands one row off looks identical to one
      // that landed right, and naming the destination and the day is how that gets caught.
      const who = board.staff.find((s) => s.id === staffId)?.name || 'That person'
      const to = board.groups.find((g) => g.id === toGroupId)?.name
      const when = formatDate(on)
      toast.success(
        to ? `${who} moved to ${to} for ${when}.` : `${who} is off every team for ${when}.`,
        { title: record ? undefined : 'Undone' },
      )
    } catch (err) {
      toast.error(err)
    }
  }

  /**
   * Opening an existing absence from the marker that draws it — the month cell's name, and
   * now the board's "Off" pill and part-day tile as well.
   *
   * `on` carries the day the marker was clicked so the dialog's "This day" preset means that
   * day rather than whichever day the page happens to be showing. Null for a reader, which
   * is what leaves the markers as plain text.
   */
  const openTimeOff = writable ? (t, on = null) => setTimeOffFor({ staffId: t.staffId, entry: t, on }) : null

  // `fields` is the dialog's whole answer — dates, hours, the note, and the scheduler-only
  // three — passed through as it is so a field added to the dialog cannot be dropped here.
  async function saveTimeOff(fields) {
    const { fromDate, toDate } = fields
    const { staffId, entry } = timeOffFor
    try {
      if (entry) {
        await api.patch(`/api/time-off/${entry.id}`, { ...fields, mode })
        setTimeOffFor(null)
        invalidate()
        toast.success('Time off updated.')
        return
      }
      await api.post('/api/time-off', { ...fields, userId: staffId, mode })
      setTimeOffFor(null)
      invalidate()
      const who = board.staff.find((s) => s.id === staffId)?.name || 'That person'
      toast.success(
        fromDate === toDate
          ? `${who} is off on ${formatDate(fromDate)}.`
          : `${who} is off ${formatDate(fromDate)} – ${formatDate(toDate)}.`,
      )
    } catch (err) {
      toast.error(err)
    }
  }

  /**
   * Several people booked off in one go, from the month view.
   *
   * Sequential rather than Promise.all: these are writes against the same table, the counts
   * involved are a handful, and if the fourth fails the first three have still landed — which
   * is worth reporting accurately rather than losing in one rejected batch.
   */
  async function addTimeOffForMany(staffIds, fields) {
    const { fromDate, toDate } = fields
    const failed = []
    let added = 0
    for (const userId of staffIds) {
      try {
        await api.post('/api/time-off', { ...fields, userId, mode })
        added++
      } catch (err) {
        failed.push(`${board.staff.find((s) => s.id === userId)?.name || 'Someone'}: ${err.message}`)
      }
    }
    setTimeOffDay(null)
    invalidate()
    if (added) {
      const when = fromDate === toDate ? formatDate(fromDate) : `${formatDate(fromDate)} – ${formatDate(toDate)}`
      toast.success(`${added} ${added === 1 ? 'person' : 'people'} booked off ${when}.`)
    }
    if (failed.length) toast.error(failed.join(' · '))
  }

  async function removeTimeOff(id) {
    // Read before the delete, off the board on screen: it is the same row the server is
    // about to remove, and it is what Undo needs to write back.
    const t = (board?.timeOff || []).find((x) => x.id === id)
    try {
      await api.del(`/api/time-off/${id}?mode=${mode}`)
      setTimeOffFor(null)
      invalidate()
      if (!t) {
        toast.success('Time off removed.')
        return
      }
      undo('Time off removed.', () =>
        api.post('/api/time-off', {
          userId: t.staffId,
          fromDate: t.from,
          toDate: t.to,
          note: t.note,
          startTime: t.startTime,
          endTime: t.endTime,
          reason: t.reason,
          requestStatus: t.requestStatus,
          internalNote: t.internalNote,
          mode,
        }),
      )
    } catch (err) {
      toast.error(err)
    }
  }

  // ── The fleet ─────────────────────────────────────────────────────────────
  // Each returns whether the write landed, the same contract as handleSave, so the popover
  // closes on a save the server accepted and stays open on one it refused.

  async function setAssetDay({ assetId, dates, staffUserId, label, internalNote }) {
    try {
      await api.put('/api/asset-assignments', { assetId, dates, staffUserId, label, internalNote })
      invalidate()
      return true
    } catch (err) {
      toast.error(err)
      return false
    }
  }

  async function clearAssetDay(assetId, assetDate) {
    try {
      await api.del(`/api/asset-assignments?assetId=${assetId}&date=${assetDate}`)
      invalidate()
      return true
    } catch (err) {
      toast.error(err)
      return false
    }
  }

  /**
   * One request for the lot, through the server's copy-day, which says what it left alone and
   * why. Undo removes exactly the rows it made, by id, so a job somebody booked by hand in the
   * meantime is never taken with them.
   */
  async function copyDayOnto({ toDates, staffUserIds, skipHolidays }) {
    const { date: fromDate, source } = copyDayFor
    const boardMode = source?.mode ?? mode
    try {
      const res = await api.post('/api/assignments/copy-day', {
        mode: boardMode,
        fromDate,
        toDates,
        staffUserIds,
        skipHolidays,
      })
      invalidate()
      setCopyDayFor(null)
      const other = modeName(boardMode === 'cat' ? 'normal' : 'cat')
      const said = [
        res.copied ? `Copied ${res.copied} ${res.copied === 1 ? 'job' : 'jobs'}.` : 'Nothing was copied.',
        res.skipped > 0 && `${res.skipped} left alone where a job was already booked.`,
        res.off > 0 && `${res.off} skipped where somebody is off all day.`,
        res.holidays?.length > 0 && `Skipped ${res.holidays.map(formatShort).join(', ')}, a statutory holiday.`,
        res.otherBoard?.length > 0 &&
          `Left ${res.otherBoard.map(formatShort).join(', ')} alone: ${other} mode is on then.`,
      ]
        .filter(Boolean)
        .join(' ')
      if (res.ids?.length) {
        undo(said, () => Promise.all(res.ids.map((id) => api.del(`/api/assignments/${id}`))))
      } else {
        toast.info(said)
      }
    } catch (err) {
      toast.error(err)
    }
  }

  async function saveAssetDowntime(existing, asset, { fromDate, toDate, reason, internalNote }) {
    try {
      if (existing) await api.patch(`/api/asset-downtime/${existing.id}`, { fromDate, toDate, reason, internalNote })
      else await api.post('/api/asset-downtime', { assetId: asset.id, fromDate, toDate, reason, internalNote })
      invalidate()
      toast.success(
        fromDate === toDate
          ? `${asset.name} is out of service on ${formatDate(fromDate)}.`
          : `${asset.name} is out of service ${formatDate(fromDate)} – ${formatDate(toDate)}.`,
      )
      return true
    } catch (err) {
      toast.error(err)
      return false
    }
  }

  async function deleteAssetDowntime(id) {
    const d = (board?.assetDowntime || []).find((x) => x.id === id)
    try {
      await api.del(`/api/asset-downtime/${id}`)
      invalidate()
      if (!d) toast.success('Back in service.')
      else {
        undo('Back in service.', () =>
          api.post('/api/asset-downtime', {
            assetId: d.assetId,
            fromDate: d.from,
            toDate: d.to,
            reason: d.reason,
            internalNote: d.internalNote,
          }),
        )
      }
      return true
    } catch (err) {
      toast.error(err)
      return false
    }
  }

  // A register change made from the board: today's exception becomes the standing arrangement
  // from today on. Earlier days keep whatever they said. Asset admins only — the menu item is
  // withheld from everyone else, and the server refuses regardless.
  async function setAssetCustody(asset, staffId, effectiveFrom) {
    try {
      await api.post(`/api/assets/${asset.id}/custody`, { staffUserId: staffId, effectiveFrom })
      invalidate()
      qc.invalidateQueries({ queryKey: ['assets'] })
      const who = board.staff.find((s) => s.id === staffId)?.name || 'That person'
      toast.success(`${who} usually has ${asset.name} from ${formatDate(effectiveFrom)}.`)
    } catch (err) {
      toast.error(err)
    }
  }

  const cutStaff = (staffId, fromGroupId) => setCut({ staffId, fromGroupId })

  /**
   * Show the cell a booking sits in — what both drawers do when a row is clicked: the note
   * you want to read in context, the conflict you want to go and fix.
   *
   * Everything is resolved HERE rather than in the drawer, because only this component knows
   * how the board is currently grouped: a drawer is handed the raw payload, where a job's
   * renderGroupId is its crew, while the cells on a people board are keyed by the one
   * synthetic band. The drawer would compute a key that matches nothing.
   *
   * The date is never touched. Both drawers list rows read out of the board on screen, so
   * everything either can offer is already inside board.days — and changing the date would
   * re-key the query, unmount the grid mid-jump, and reset the collapse and pin state that
   * this is about to rely on.
   */
  const revealOnBoard = (a) => {
    if (!a || !board) return
    const closeDrawers = () => { setNotesOpen(false); setConflictsOpen(false) }
    // The month view draws no cells at all, so there is nothing to point at. Its own day
    // dialog is the honest answer: it shows that day, with the job on it.
    if (isMonth) {
      closeDrawers()
      setDayModal(a.date)
      return
    }
    const drawn = view === 'day' ? a.date === date : (board.days || []).some((d) => d.date === a.date)
    if (!drawn) {
      // Should not happen while a drawer reads from this board, but saying so is better
      // than a click that quietly does nothing.
      toast.error('That job is on a day this view is not showing.')
      return
    }
    // The band the cell is drawn under, in the shape the grid keys by.
    const bandId = byPeople ? PEOPLE_BAND_ID : a.renderGroupId
    const cellKey = `${bandId || 'none'}:${a.staffId}:${view === 'day' ? a.slot : a.date}`
    // Folded away, the cell is not rendered at all — so open its band first. Not gated on
    // mode: a band folded in Team mode has no toggle of its own to reopen it.
    setCollapsedGroups((prev) => {
      const key = bandId ?? 'unassigned'
      if (!prev.has(key)) return prev
      const next = new Set(prev)
      next.delete(key)
      return next
    })
    closeDrawers()
    setReveal({ entryId: a.id, cellKey, token: ++revealSeq.current })
  }

  /**
   * "We know, it is deliberate."
   *
   * Undoable like every other click that takes something off a list, and for the stronger
   * reason here: settling a conflict hides it from everybody, and a misfire would otherwise
   * be unrecoverable without knowing this panel existed in the first place.
   */
  async function resolveConflict(c) {
    const body = { assignmentId: c.assignment.id, timeOffId: c.timeOff.id, mode }
    try {
      await api.post('/api/conflicts/resolve', body)
      invalidate()
      undo('Marked as deliberate.', () => api.post('/api/conflicts/unresolve', body))
    } catch (err) {
      toast.error(err)
    }
  }

  /**
   * Changing that decision back.
   *
   * The same call the toast's Undo makes, reachable from the settled card itself — the toast
   * is gone within seconds, and "we decided this was fine" is exactly the kind of decision
   * somebody revisits a week later. The toast here says the opposite thing and offers the
   * opposite undo, so the pair can be walked in either direction.
   */
  async function unresolveConflict(c) {
    const body = { assignmentId: c.assignment.id, timeOffId: c.timeOff.id, mode }
    try {
      await api.post('/api/conflicts/unresolve', body)
      invalidate()
      undo('Put back on the list.', () => api.post('/api/conflicts/resolve', body))
    } catch (err) {
      toast.error(err)
    }
  }

  /**
   * Applied after render, not in the click handler: expanding a band and closing the drawer
   * are state changes, so the cell does not exist in the DOM until React has painted them.
   * One retry on the next frame covers a grid that settles its own layout late (the week
   * grid measures its merged bars in a layout effect of its own).
   */
  useLayoutEffect(() => {
    if (!reveal || revealDone.current === reveal.token) return
    let frame = 0
    const attempt = () => {
      const cell = document.querySelector(`[data-cell-key="${CSS.escape(reveal.cellKey)}"]`)
      if (!cell) {
        if (frame++ < 2) return requestAnimationFrame(attempt)
        revealDone.current = reveal.token
        toast.error('That job is not drawn on the board right now — the person may be booked off that day.')
        setReveal(null)
        return
      }
      revealDone.current = reveal.token
      revealCell(cell)
    }
    attempt()
  }, [reveal, board, view, date, groupBy, collapsedGroups, toast])

  /**
   * The mark is a blink, not a selection: it says "here" and then gets out of the way, so
   * nothing is left looking picked once the eye has landed.
   *
   * Its own effect, keyed on the token alone. Living inside the placement effect above, its
   * cleanup was cancelled the moment ANY of that effect's dependencies changed — and
   * revealing a note changes one of them itself, since expanding a band hands back a new
   * collapsedGroups Set. The re-run then hit the already-done guard and returned before
   * re-arming, so the highlight simply stayed on forever.
   */
  useEffect(() => {
    if (!reveal) return undefined
    const t = setTimeout(() => setReveal(null), REVEAL_MS)
    return () => clearTimeout(t)
  }, [reveal?.token])

  // The highlight is about one board in one arrangement. Anything that changes what is drawn
  // takes it with it, rather than leaving a ring on whichever cell inherits that key.
  useEffect(() => { setReveal(null) }, [view, date, groupBy, mode])

  // And it goes when the job it points at does — deleted, moved, or refetched away.
  useEffect(() => {
    if (!reveal || !board) return
    if (!(board.assignments || []).some((a) => a.id === reveal.entryId)) setReveal(null)
  }, [board, reveal])

  const toggleCollapse = (groupKey) => setCollapsedGroups((prev) => toggleInSet(prev, groupKey))

  // Crew bands fold only when the board is grouped by crew — grouped by person there is one
  // band holding everybody, and folding it would hide the whole schedule.
  const groupKeys = groupBy === 'group' ? board?.groups.map((g) => g.id ?? 'unassigned') || [] : []
  /**
   * The fleet's bands, which fold on their own key set and are not affected by how the crews
   * are grouped — an asset group is an asset group either way.
   *
   * Only bands that are actually drawn: an asset group nothing is in gets no heading, so
   * counting it would leave "collapse all" believing something was still open and offering
   * to expand when everything was already shut.
   */
  const fleetKeys = useMemo(() => {
    const assets = board?.assets || []
    if (!assets.length) return []
    const drawn = (board.assetGroups || [])
      .filter((g) => assets.some((a) => (a.groupIds || []).includes(g.id)))
      .map((g) => g.id)
    return assets.some((a) => !(a.groupIds || []).length) ? [...drawn, UNGROUPED] : drawn
  }, [board])

  const foldable = groupKeys.length + fleetKeys.length
  const allCollapsed =
    foldable > 0 &&
    groupKeys.every((k) => collapsedGroups.has(k)) &&
    fleetKeys.every((k) => fleetCollapsed.has(k))
  // One gesture for the whole board, crews and fleet together — they are bands on the same
  // page, and folding them separately was two trips to the same menu.
  const toggleAllCollapsed = () => {
    const shut = !allCollapsed
    setCollapsedGroups(shut ? new Set(groupKeys) : new Set())
    setFleetCollapsed(shut ? new Set(fleetKeys) : new Set())
  }

  // Notifying staff is the point of the morning: the board is filled so that people know
  // where to go. So it is the page's primary action — but only once there is something to
  // tell them. An empty board has no message to send, and a primary button that fires off a
  // notification about nothing is worse than no button.
  //
  // Scoped to what is on screen: the day in day view, the whole span otherwise. The count comes
  // from the same query that drew the grid, so it can never disagree with what is visible.
  const notifiableJobs = useMemo(
    () => (board?.assignments || []).filter((a) => (isMulti ? true : a.date === date)).length,
    [board, view, date],
  )

  // Sending is a one-way action to everybody's inbox, so it is confirmed — and the confirm
  // dialog is populated by asking the server what it WOULD send, rather than by counting rows
  // on screen. The list shown is therefore exactly what gets emailed, including when this
  // person's own view of the board is narrower than the board itself — and it is the starting
  // point for editing, not just a readout: the preview's `recipients` becomes local state the
  // moment it loads, so removing someone or adding an office line is just editing that array
  // before Send ever sees it.
  async function openNotify() {
    setNotifyBusy(true)
    // The preview builds the whole board and renders the email, so it is not instant. That
    // wait used to be covered by the primary button greying out; now that Notify is a menu
    // item, the menu has closed by the time the request is in flight and nothing on screen
    // says anything is happening. Held open (duration 0) and dismissed below, so it lasts
    // exactly as long as the wait does.
    const waiting = toast.info('Preparing the schedule email…', { duration: 0 })
    try {
      const preview = await api.post('/api/notify/preview', { mode, from, to })
      setNotify({
        ...preview,
        before: '',
        after: '',
        showBefore: false,
        showAfter: false,
        editingRecipients: false,
        addEmail: '',
        attachments: [],
      })
      previewedRef.current = { before: '', after: '' }
    } catch (err) {
      toast.error(err)
    } finally {
      if (waiting) toast.dismiss(waiting)
      setNotifyBusy(false)
    }
  }

  function removeRecipient(email) {
    setNotify((n) => ({ ...n, recipients: n.recipients.filter((r) => r.email !== email) }))
  }

  // A plain email is accepted alone — the name falls back to the address itself, same as the
  // server does if this somehow reached it unnamed anyway.
  function addRecipient() {
    const email = notify.addEmail.trim().toLowerCase()
    if (!email) return
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('That does not look like an email address.')
      return
    }
    if (notify.recipients.some((r) => r.email === email)) {
      toast.error('Already on the list.')
      return
    }
    setNotify((n) => ({ ...n, recipients: [...n.recipients, { email, name: email }], addEmail: '' }))
  }

  /**
   * Files are read here and sent as part of the message, never uploaded and referenced.
   * Nothing is stored on the server, so there is no upload directory to secure and nothing
   * left behind when somebody opens this dialog and then changes their mind.
   */
  async function addAttachments(fileList) {
    const files = [...(fileList || [])]
    if (!files.length) return
    try {
      const read = await Promise.all(
        files.map(
          (file) =>
            new Promise((resolve, reject) => {
              const reader = new FileReader()
              // readAsDataURL gives "data:<type>;base64,<payload>" — the payload is exactly
              // the contentBytes Graph wants, so there is nothing to convert.
              reader.onload = () =>
                resolve({
                  name: file.name,
                  contentType: file.type || 'application/octet-stream',
                  size: file.size,
                  contentBytes: String(reader.result).split(',')[1] || '',
                })
              reader.onerror = () => reject(new Error(`Could not read ${file.name}`))
              reader.readAsDataURL(file)
            }),
        ),
      )
      setNotify((n) => {
        // Same name twice is almost always the same file picked again, not two files.
        const have = new Set(n.attachments.map((a) => a.name))
        return { ...n, attachments: [...n.attachments, ...read.filter((a) => !have.has(a.name))] }
      })
    } catch (err) {
      toast.error(err)
    }
  }

  const removeAttachment = (name) =>
    setNotify((n) => ({ ...n, attachments: n.attachments.filter((a) => a.name !== name) }))

  /**
   * Keep the preview honest as the notes are typed.
   *
   * Re-asks the SERVER to compose rather than rendering the notes into the preview here:
   * they are sanitised on the way in, so a client-side preview would show markup that the
   * real email will not carry. Anything the allowlist drops has to disappear here too, or
   * the preview quietly becomes a lie about what the crew will receive.
   *
   * Keyed on the notes alone — the subject never reaches the body, and the schedule itself
   * comes from the board, which has its own query and its own invalidation.
   */
  const previewedRef = useRef({ before: null, after: null })
  const previewSeqRef = useRef(0)
  useEffect(() => {
    if (!notify) return undefined
    const before = notify.before ?? ''
    const after = notify.after ?? ''
    // openNotify already fetched this exact pair; do not immediately ask again.
    if (previewedRef.current.before === before && previewedRef.current.after === after) {
      return undefined
    }
    const seq = ++previewSeqRef.current
    const timer = setTimeout(async () => {
      setPreviewBusy(true)
      try {
        const p = await api.post('/api/notify/preview', { mode, from, to, before, after })
        // A slower earlier request must not overwrite a faster later one — typing produces
        // overlapping requests, and the last one STARTED is the only one still true.
        if (seq !== previewSeqRef.current) return
        previewedRef.current = { before, after }
        setNotify((n) => (n ? { ...n, html: p.html } : n))
      } catch {
        // Leave the last good preview on screen. A failed refresh is not worth replacing a
        // readable preview with an error, and Send reports its own failures properly.
      } finally {
        if (seq === previewSeqRef.current) setPreviewBusy(false)
      }
    }, 400)
    return () => clearTimeout(timer)
  }, [notify, mode, from, to])

  async function sendNotify() {
    setNotifyBusy(true)
    try {
      const res = await api.post('/api/notify', {
        mode,
        from,
        to,
        subject: notify.subject,
        before: notify.before,
        after: notify.after,
        recipients: notify.recipients,
        attachments: notify.attachments.map(({ name, contentType, contentBytes }) => ({
          name,
          contentType,
          contentBytes,
        })),
      })
      setNotify(null)
      const n = res.recipients.length
      toast.success(`Sent to ${n} ${n === 1 ? 'person' : 'people'}.`)
    } catch (err) {
      toast.error(err)
    } finally {
      setNotifyBusy(false)
    }
  }

  // Escape clears a pending cut or copy — the same key every other menu and dialog in this
  // app already uses to back out of something. Ctrl/Cmd+Z reverses the last reorg move, as
  // long as focus isn't in a text field, where the browser's own undo should win instead.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && (cut || clip)) { setCut(null); setClip(null); return }
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
        const el = document.activeElement
        if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return
        if (!moveHistory.length) return
        e.preventDefault()
        const last = moveHistory[moveHistory.length - 1]
        setMoveHistory((h) => h.slice(0, -1))
        // Undone against the date it was made on, not whatever day is on screen now.
        moveStaffToTeam(last.staffId, last.toGroupId, last.fromGroupId, {
          record: false,
          on: last.on,
        })
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [cut, clip, moveHistory])

  if (!date) return <div className="page empty">Loading…</div>

  const attachTotal = (notify?.attachments || []).reduce((n, a) => n + (a.size || 0), 0)
  const attachTooBig = attachTotal > MAX_ATTACH_BYTES

  return (
    <div className="page board-page" ref={pageRef}>
      {/* Title, then what you are looking at, then what you can do to it — left to right, the
          way a macOS toolbar reads. The date moved up here out of the h2 it used to have below
          the header: it is the answer to "which day am I on", and it belongs where it stays
          visible while the board scrolls rather than sliding away with the first row. */}
      <div className="page-head board-head no-print" ref={headRef}>
        <div className="board-head-title head-titled">
          {/* The same tile a list wears on its own page, so the board reads as one of the
              boards rather than as a different kind of screen — in grey, because the colours
              belong to the lists. See TILE.board. */}
          <GlyphTile {...TILE.board} size={36} />
          <div className="head-titled-text">
          <h1>Board</h1>
          <p className="board-head-when">
            {view === 'day'
              ? formatDate(date, { withYear: true })
              : view === 'month'
                ? formatMonth(month)
                : view === '2week'
                  ? `${formatDate(span[0])} – ${formatDate(span[span.length - 1])}`
                  : `Week of ${formatDate(span[0])}`}
            {/* Beside the period, because that is what it counts: the conflicts on the days
                this board is currently showing. Silent when there are none — a badge that is
                always there is furniture, and furniture gets read past. */}
            {conflictCount > 0 && (
              <button
                type="button"
                className="conflict-pill"
                onClick={() => setConflictsOpen(true)}
                title="See what is booked on a day somebody is away"
              >
                <Icon name="badgeAlert" size={13} strokeWidth={2.2} />
                {conflictCount} {conflictCount === 1 ? 'conflict' : 'conflicts'}
              </button>
            )}
            {dayMode?.label && (
              <span className={`tag ${mode === 'cat' ? 'cat' : 'owner'}`}>{dayMode.label}</span>
            )}
            {view === 'day' && (
              <HolidayNote
                holiday={board?.days?.find((d) => d.date === date)?.holiday || null}
                date={date}
                mode={mode}
                writable={writable}
                onChanged={invalidate}
              />
            )}
          </p>
          </div>
        </div>

        <div className="spacer" />

        {/* Which day, and how much of it. Both are "change what is on screen", so they sit
            together and apart from the actions on the far side of the divider. */}
        <div className="board-head-group">
          {/* One press moves by whatever the view shows — a day, a week, a fortnight — so
              "next" always means "the next screenful" rather than always meaning tomorrow. */}
          <DayNav
            date={date}
            setDate={setDate}
            today={currentMode.today}
            step={pageStep}
            stepBy={isMonth ? stepMonth : undefined}
          />
          <DateField
            value={date}
            onChange={setDate}
            today={currentMode.today}
            weekStart={tenant?.weekStart || 1}
            label="Board date"
          />
          <div className="segmented">
            <button className={view === 'day' ? 'selected' : ''} onClick={() => setView('day')}>Day</button>
            <button className={view === 'week' ? 'selected' : ''} onClick={() => setView('week')}>Week</button>
            <button className={view === '2week' ? 'selected' : ''} onClick={() => setView('2week')}>2 weeks</button>
            <button className={view === 'month' ? 'selected' : ''} onClick={() => setView('month')}>Month</button>
          </div>
        </div>

        <span className="board-head-divider" aria-hidden="true" />

        {/* Print takes the fill: it is what this row is reached for most, and it is the one
            action available on every view and to every reader. Notify sits in the menu with
            the rest — it leaves the building, but only a few people may send it and only on
            a board with something booked, and a permanently-lit button that is disabled for
            most of the company most of the time is not a primary action. */}
        <div className="board-head-group">
          {isMonth && board && (
            // Only on the month view, and only there because it is the only view that draws
            // people as a flat list rather than as rows under their crew — everywhere else
            // the team bands already answer "whose schedule am I looking at".
            <MultiSelectDropdown
              value={people}
              onToggle={togglePerson}
              onClear={() => setPeople(new Set())}
              options={peopleOptions}
              // "People" rather than "Everyone": the summary form appends a count, and
              // "Everyone (2)" says two contradictory things at once.
              placeholder="People"
              summary
              searchable
            />
          )}
          <SplitButton
            onClick={() => setPrinting(true)}
            disabled={!board}
            title={board ? undefined : 'Nothing to print yet'}
            items={[
              {
                key: 'notify',
                label: 'Notify staff',
                icon: 'bell',
                // Disabled with the reason rather than hidden, unlike most of this menu:
                // the three ways it can be unavailable are all things somebody would
                // otherwise go looking for the missing button over.
                disabled: notifyBusy || isMonth || !can.notifyStaff || notifiableJobs === 0,
                title:
                  // Notify emails the schedule for what is on screen. On the month view
                  // what is on screen is a list of names, not a board anybody could work a
                  // shift from — so there is nothing here it would be honest to send.
                  isMonth
                    ? 'Not available on the month view'
                    : !can.notifyStaff
                      ? 'You are not set up to send schedule emails'
                      : notifiableJobs === 0
                        ? 'Nothing booked here yet'
                        : undefined,
                onClick: openNotify,
              },
              {
                key: 'notes',
                label: 'Show Internal notes',
                icon: 'note',
                // Only for someone who can write this board — the same people the server
                // sends the notes to at all. On a read-only board the field is not in the
                // payload, so the drawer would open on an empty list and read as "no notes"
                // rather than "not yours to see".
                show: Boolean(board) && writable,
                onClick: () => setNotesOpen(true),
              },
              {
                key: 'collapse',
                label: allCollapsed ? 'Expand all' : 'Collapse all',
                icon: allCollapsed ? 'down' : 'up',
                // Offered whenever there is anything to fold — crew bands, the fleet's, or
                // both. Grouped by person the crews are one band and contribute nothing, so
                // this can still appear there for the fleet alone.
                show: mode === 'cat' && view !== 'month' && foldable > 0,
                onClick: toggleAllCollapsed,
              },
              {
                key: 'groupby',
                label: 'Categorize by',
                icon: 'sort',
                // Group mode only: a Team-mode board is already one row per person, and its
                // bands are the PMs who own the work rather than a second thing to colour.
                show: mode === 'cat' && Boolean(board),
                children: [
                  {
                    key: 'gb-group',
                    // "Groups", not the tenant's word for the mode. This pair names the two
                    // AXES a board can be organised along, and both halves have to be read
                    // the same way — against "People", a tenant-renamed "CATs" read as the
                    // name of a thing rather than as a way of arranging the rows.
                    label: 'Groups',
                    checked: groupBy === 'group',
                    onClick: () => setGroupBy('group'),
                  },
                  {
                    key: 'gb-people',
                    label: 'People',
                    checked: groupBy === 'people',
                    onClick: () => setGroupBy('people'),
                  },
                ],
              },
              {
                key: 'order',
                label: 'Board view settings',
                icon: 'sliders',
                // Both modes, both arrangements: every one of them draws rows, and the fleet is
                // on the Team board too. Not on the month view, which lists each day's names
                // alphabetically and has no rows to put in order.
                show: Boolean(board) && !isMonth,
                onClick: () => setOrderOpen(true),
              },
              {
                key: 'clear',
                label: 'Clear filters',
                icon: 'close',
                show: Boolean(board) && (mode === 'cat' || isMonth),
                disabled: !hasFilters,
                title: hasFilters ? undefined : 'Nothing is filtered right now',
                onClick: clearFilters,
              },
            ]}
          >
            <Icon name="printer" size={16} /> Print
          </SplitButton>
        </div>
      </div>

      {!writable && board && (
        <div className="notice no-print">
          You have read-only access to this board.
          {/* Named after the role that actually grants it. Leading a crew grants nothing on
              its own since the Group admin role replaced the old coordinator role — see
              shared/scope.js — so "coordinators" here was sending people to ask the wrong
              person. */}
          {mode === 'cat' && ` In ${modeName('cat')} mode only a ${roleLabel('group_admin')} or an owner can schedule.`}
        </div>
      )}

      {/* A copied job stays copied until it is let go, so it can be pasted along a week. This
          strip is what makes that safe: without it, a held copy is invisible, and a Paste
          offered days later is a surprise. */}
      {clip && writable && (
        <div className="notice no-print list-clip-notice">
          <Icon name="copy" size={14} />
          <span>
            Copied <strong>{clip.label}</strong>. Right-click any cell to paste it, as often as you
            need. Escape lets it go.
          </span>
          <button className="btn sm ghost" onClick={() => setClip(null)}>Done</button>
        </div>
      )}

      {isLoading || !board ? (
        <div className="empty">Loading board…</div>
      ) : shownBoard.groups.length === 0 ? (
        <div className="card empty">Nobody to show. Add field staff and put them on a team.</div>
      ) : view === 'month' ? (
        <MonthGrid
          board={shownBoard}
          month={month}
          weekStart={tenant?.weekStart || 1}
          today={currentMode.today}
          staffById={staffById}
          staffFilter={people.size ? people : null}
          onOpenDay={setDayModal}
          onAddDay={writable ? setTimeOffDay : null}
          onEditTimeOff={openTimeOff}
        />
      ) : view === 'day' ? (
        <div className="board-scroll">
        <DayGrid
          board={shownBoard}
          crews={byPeople ? crews : null}
          revealedKey={reveal?.cellKey || null}
          date={date}
          editing={editing}
          setEditing={setEditing}
          canCreateIn={(group) => canCreate(user, scopeCtx, { catTeamId: group?.id })}
          canEdit={(entry) => canMutate(user, scopeCtx, entry)}
          onSave={handleSave}
          onDelete={handleDelete}
          onAddColumn={writable ? addColumn : null}
          canManageTeams={Boolean(can?.manageCatTeams)}
          cut={cut}
          onCutStaff={cutStaff}
          onMoveStaff={moveStaffToTeam}
          collapsedGroups={collapsedGroups}
          onToggleCollapse={toggleCollapse}
          pins={pins}
          onSetPins={setPins}
          sideWidths={sideWidths}
          onSetSideWidths={setSideWidths}
          clip={clip}
          onCopyEntry={setClip}
          onCopyDay={writable ? (staffId, on) => setCopyDayFor({ staffId, date: on, source: board }) : null}
          onAddTimeOff={writable ? (staffId, on) => setTimeOffFor({ staffId, entry: null, on }) : null}
          onEditTimeOff={openTimeOff && ((t) => openTimeOff(t, date))}
        />
        {/* Present only when the company draws its assets on the board and this person may
            see them — the key is absent from the payload otherwise, so this is the whole
            test. Drawn from `orderedBoard`, not `shownBoard`: grouping the crews by person
            changes nothing here, but the fleet's order from Board view settings does. */}
        {board.assets && (
          <FleetDayTable
            board={orderedBoard}
            date={date}
            fleet={fleet}
            collapsed={fleetCollapsed}
            onToggleCollapse={toggleFleetBand}
          />
        )}
        </div>
      ) : (
        <WeekGrid
          board={shownBoard}
          crews={byPeople ? crews : null}
          revealedKey={reveal?.cellKey || null}
          collapsedGroups={collapsedGroups}
          onToggleCollapse={toggleCollapse}
          wide={view === '2week'}
          afterGroups={
            board.assets ? (
              <FleetWeekRows
                board={orderedBoard}
                days={shownBoard.days}
                fleet={fleet}
                collapsed={fleetCollapsed}
                onToggleCollapse={toggleFleetBand}
              />
            ) : null
          }
          onPickDay={(d) => { setDate(d); setView('day') }}
          onAddTimeOff={writable ? (staffId, on) => setTimeOffFor({ staffId, entry: null, on }) : null}
          onEditTimeOff={openTimeOff && ((t) => openTimeOff(t, date))}
          canCreateIn={(group) => canCreate(user, scopeCtx, { catTeamId: group?.id })}
          canEdit={(entry) => canMutate(user, scopeCtx, entry)}
          onSave={handleSave}
          onDelete={handleDelete}
          clip={clip}
          onCopyEntry={setClip}
          onCopyDay={writable ? (staffId, on) => setCopyDayFor({ staffId, date: on, source: board }) : null}
        />
      )}

      {fleet.overlays}

      {orderOpen && board && (
        <BoardOrderDialog
          board={board}
          mode={mode}
          groupBy={byPeople ? 'people' : 'group'}
          order={order}
          assetsName={assetsName()}
          unitName={assetUnit()}
          onSave={(next) => {
            saveOrder(next)
            setOrderOpen(false)
          }}
          onClose={() => setOrderOpen(false)}
        />
      )}

      {copyDayFor && (() => {
        const src = copyDayFor.source || board
        const onDay = (src?.assignments || []).filter((a) => a.date === copyDayFor.date)
        const person = (src?.staff || []).find((p) => p.id === copyDayFor.staffId)
        return (
          <CopyDayModal
            date={copyDayFor.date}
            person={person ? { id: person.id, name: person.name } : null}
            personJobs={onDay
              .filter((a) => a.staffId === copyDayFor.staffId)
              .sort((a, b) => a.slot - b.slot)
              .map((a) => a.label)}
            everyoneCount={onDay.length}
            weekStart={tenant?.weekStart || 1}
            workDays={workDays || [1, 2, 3, 4, 5]}
            onSubmit={copyDayOnto}
            onClose={() => setCopyDayFor(null)}
          />
        )
      })()}

      {printing && board && (
        <PrintDialog
          board={shownBoard}
          crews={byPeople ? crews : null}
          date={date}
          view={view}
          onClose={() => setPrinting(false)}
        />
      )}

      {notesOpen && board && (
        <InternalNotesDrawer board={board} onClose={() => setNotesOpen(false)} onSelect={revealOnBoard} />
      )}

      {conflictsOpen && board && (
        <ConflictsDrawer
          board={board}
          staffById={staffById}
          canResolve={writable}
          onClose={() => setConflictsOpen(false)}
          // The absence opens over the panel rather than instead of it: settling one
          // conflict out of six should not cost you the list of the other five.
          onEditTimeOff={(c) =>
            setTimeOffFor({ staffId: c.assignment.staffId, entry: c.timeOff, on: c.date })
          }
          onRemoveBooking={handleDelete}
          onResolve={resolveConflict}
          onUnresolve={unresolveConflict}
        />
      )}

      {/* `on` is the day the menu was opened from, when it was opened from a cell. Booking
          somebody off is nearly always about the day being looked at, and defaulting to
          today meant retyping the date on every view but the current one. */}
      {timeOffFor && board && (
        <TimeOffModal
          staff={board.staff.find((s) => s.id === timeOffFor.staffId)}
          date={timeOffFor.on || date}
          weekStart={tenant?.weekStart || 1}
          editing={timeOffFor.entry}
          existing={(board.timeOff || []).filter((t) => t.staffId === timeOffFor.staffId)}
          bookings={(board.assignments || []).filter((a) => a.staffId === timeOffFor.staffId)}
          jobTypes={board.jobTypes || []}
          onSave={saveTimeOff}
          onDelete={removeTimeOff}
          onClose={() => setTimeOffFor(null)}
        />
      )}

      {dayModal && (
        <Modal
          title={
            <>
              {formatDate(dayModal, { withYear: true })}
              <HolidayNote
                holiday={dayBoard?.days?.[0]?.holiday || null}
                date={dayModal}
                mode={mode}
                writable={writable}
                onChanged={invalidate}
              />
            </>
          }
          onClose={() => setDayModal(null)}
          width={1180}
        >
          {/* The same DayGrid the Day view renders, with the same handlers behind it — so
              this edits the board rather than resembling it, and nothing here can drift away
              from what the full-page view does. Pins and collapsing are deliberately inert:
              both are standing arrangements of the page you came from, and a dialog opened to
              look at one day is not where you rearrange them. */}
          {!dayBoard ? (
            <div className="empty">Loading…</div>
          ) : (
            <DayGrid
              board={asPeopleBoard(applyBoardOrder(dayBoard, orderNow))}
              crews={byPeople ? crews : null}
              date={dayModal}
              editing={editing}
              setEditing={setEditing}
              canCreateIn={(group) => canCreate(user, scopeCtx, { catTeamId: group?.id })}
              canEdit={(entry) => canMutate(user, scopeCtx, entry)}
              onSave={handleSave}
              onDelete={handleDelete}
              onAddColumn={writable ? addColumn : null}
              canManageTeams={false}
              cut={null}
              onCutStaff={null}
              onMoveStaff={null}
              collapsedGroups={EMPTY_SET}
              onToggleCollapse={NOOP}
              pins={EMPTY_PINS}
              onSetPins={NOOP}
              sideWidths={sideWidths}
              onSetSideWidths={NOOP}
              clip={clip}
              onCopyEntry={setClip}
              onCopyDay={writable ? (staffId, on) => setCopyDayFor({ staffId, date: on, source: dayBoard }) : null}
              onAddTimeOff={writable ? (staffId, on) => setTimeOffFor({ staffId, entry: null, on }) : null}
              onEditTimeOff={openTimeOff && ((t) => openTimeOff(t, dayModal))}
            />
          )}
        </Modal>
      )}

      {timeOffDay && board && (
        <DayTimeOffModal
          date={timeOffDay}
          staff={board.staff}
          timeOff={board.timeOff || []}
          weekStart={tenant?.weekStart || 1}
          onSubmit={addTimeOffForMany}
          onClose={() => setTimeOffDay(null)}
        />
      )}

      {notify && (
        <Modal
          title="Notify"
          onClose={() => setNotify(null)}
          width={1080}
          className="modal-wide notify-modal"
        >
          <div className="notify-panes">
            <div className="notify-settings">
              <div className="notify-head">
                <img src={outlookIcon} alt="" className="notify-outlook-mark" />
                <p className="sub">One email, sent through Outlook, to everyone listed below.</p>
              </div>

              <div className="settings-list notify-list">
            <SettingsRow label="Recipients">
              <div className="notify-recipients-cell">
                <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
                  <span>
                    {notify.recipients.length} {notify.recipients.length === 1 ? 'person' : 'people'},{' '}
                    {notify.copy === 'bcc' ? 'each as a blind copy' : 'all copied in'}
                  </span>
                  <button
                    type="button"
                    className="icon-btn"
                    title="Customize recipients"
                    aria-expanded={notify.editingRecipients}
                    onClick={() => setNotify({ ...notify, editingRecipients: !notify.editingRecipients })}
                  >
                    <Icon name="edit" size={14} />
                  </button>
                </div>
                {/* Where a reply lands is a property of the list above it, so it sits inside
                    the same row. It used to follow the row as a sibling, which put it on the
                    far side of that row's border — the separator ran through the middle of
                    it rather than under the pair. */}
                <div className="notify-replyto">
                  Replies go to{' '}
                  {notify.replyTo ? (
                    <strong>{notify.replyTo}</strong>
                  ) : (
                    <span className="warn-pill sm">not configured</span>
                  )}
                </div>
              </div>
            </SettingsRow>

            {notify.editingRecipients && (
              <div className="notify-recipients-editor">
                <div className="crew">
                  {notify.recipients.length === 0 && (
                    <span className="crew-empty">Nobody on this list yet.</span>
                  )}
                  {notify.recipients.map((r) => (
                    <span key={r.email} className="crew-chip" title={r.email}>
                      {r.name}
                      <button title="Remove" onClick={() => removeRecipient(r.email)}>
                        <Icon name="close" size={12} strokeWidth={2.2} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="row" style={{ gap: 6, marginTop: 8 }}>
                  <input
                    type="email"
                    placeholder="office@yourcompany.com"
                    value={notify.addEmail}
                    onChange={(e) => setNotify({ ...notify, addEmail: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRecipient() } }}
                  />
                  <button type="button" className="btn sm" onClick={addRecipient}>Add</button>
                </div>
              </div>
            )}

            <SettingsRow label="Subject">
              <input
                type="text"
                value={notify.subject}
                onChange={(e) => setNotify({ ...notify, subject: e.target.value })}
              />
            </SettingsRow>

            <SettingsRow label="Note before" stacked={notify.showBefore}>
              {notify.showBefore ? (
                <RichText
                  autoFocus
                  placeholder="Anything to say before the schedule…"
                  value={notify.before}
                  onChange={(html) => setNotify((n) => ({ ...n, before: html }))}
                />
              ) : (
                <AddNoteButton onClick={() => setNotify({ ...notify, showBefore: true })} />
              )}
            </SettingsRow>

            <SettingsRow label="Note after" stacked={notify.showAfter}>
              {notify.showAfter ? (
                <RichText
                  autoFocus
                  placeholder="Anything to say after the schedule…"
                  value={notify.after}
                  onChange={(html) => setNotify((n) => ({ ...n, after: html }))}
                />
              ) : (
                <AddNoteButton onClick={() => setNotify({ ...notify, showAfter: true })} />
              )}
            </SettingsRow>

            <SettingsRow label="Attachments" stacked={notify.attachments.length > 0}>
              {notify.attachments.length > 0 && (
                <ul className="notify-attachments">
                  {notify.attachments.map((a) => (
                    <li key={a.name}>
                      <Icon name="paste" size={14} />
                      <span className="notify-attachment-name" title={a.name}>{a.name}</span>
                      <span className="notify-attachment-size">{fileSize(a.size)}</span>
                      <button
                        type="button"
                        className="icon-btn"
                        title={`Remove ${a.name}`}
                        onClick={() => removeAttachment(a.name)}
                      >
                        <Icon name="close" size={13} strokeWidth={2.2} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="row notify-add-row">
                <label className="btn ghost sm">
                  <Icon name="plus" size={13} /> Add files
                  <input
                    type="file"
                    multiple
                    hidden
                    onChange={(e) => { addAttachments(e.target.files); e.target.value = '' }}
                  />
                </label>
                {attachTotal > 0 && (
                  <span className={attachTooBig ? 'warn-pill' : 'hint'}>
                    {fileSize(attachTotal)}
                    {attachTooBig ? ` — over the ${fileSize(MAX_ATTACH_BYTES)} limit` : ''}
                  </span>
                )}
              </div>
            </SettingsRow>
              </div>
            </div>

            {/* The right-hand pane is the message itself, rendered by the SERVER's composer
                and returned by the dry run — not a second renderer's impression of it. A
                preview that can disagree with what gets sent is worse than no preview. */}
            <div className="notify-preview">
              <div className="notify-preview-head">
                <span>Preview</span>
                {previewBusy && <span className="hint">updating…</span>}
              </div>
              {/* The subject is not part of the composed body, but it is part of what lands
                  in an inbox — shown the way a mail client stacks it, above the message. */}
              <div className="notify-preview-subject" title={notify.subject}>
                {notify.subject}
              </div>
              {notify.html ? (
                // An iframe, because this is a whole document's worth of inline styling
                // written for mail clients: in the page it would both inherit the app's CSS
                // and leak its own into everything around it.
                <iframe
                  className="notify-preview-frame"
                  title="Email preview"
                  sandbox=""
                  srcDoc={previewDoc(notify.html)}
                />
              ) : (
                <div className="empty">Nothing to preview yet.</div>
              )}
            </div>
          </div>

          <div className="modal-actions">
            <button className="btn" onClick={() => setNotify(null)} disabled={notifyBusy}>Cancel</button>
            <button
              className="btn primary"
              onClick={sendNotify}
              disabled={notifyBusy || attachTooBig}
              title={attachTooBig ? 'Remove an attachment first — the email is over the size limit' : undefined}
            >
              <Icon name="send" size={14} />
              {notifyBusy ? 'Sending…' : 'Send it'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  )
}
